import { QueryKey, useQuery } from '@tanstack/react-query';

import { fetcher } from 'src/utils/axios';

// ----------------------------------------------------------------------
// One read-then-shape seam — the query counterpart to useEntityMutation.
//
// ~47 of the 50 useGet* hooks used to retype the same dance: pull the Auth0
// token, stuff it into the queryKey, call `fetcher` (or return null when the
// id is missing), and pass the same staleTime/gcTime knobs. This hook absorbs
// that dance once. Each call site supplies only what varies:
//   - queryKey:  the cache identity (e.g. ['receptions', id])
//   - endpoint:  the resolved URL, or null to disable the read (the id-guard)
//   - enabled:   optional override (defaults to "endpoint is non-null")
//   - staleTime / gcTime: cache knobs, forwarded verbatim
//
// It returns the FULL React Query status set so callers surface whatever they
// already surfaced (some expose isLoading, some isFetching). The `<T>` cast and
// the named-return key (`{ reception }`, `{ client }`, …) stay in the thin
// wrapper, so no call site changes.
//
// Deliberately NOT exposed: select / refetchInterval / options pass-through.
// A hook that needs real post-fetch logic can't use this — and rightly stays
// full-size and legible (see the 3 KEEP hooks in handoff-05).
// ----------------------------------------------------------------------

export interface UseEntityQueryOptions {
  /** Cache identity, e.g. ['receptions', receptionId]. */
  queryKey: QueryKey;
  /** Resolved request URL, or null to disable the read (caller's id-guard). */
  endpoint: string | null;
  /** Override the run condition. Defaults to `!!endpoint`. */
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

/**
 * Pure: turns the caller's options into the object handed to `useQuery`. Owns the
 * endpoint/enabled guard. The queryKey is the caller's verbatim. The Auth0 token is
 * no longer threaded here — the axios request interceptor attaches it per-request.
 * `fetch` is injectable so the builder can be unit-tested without React or Auth0.
 */
export function buildEntityQueryArgs(
  options: UseEntityQueryOptions,
  fetch: typeof fetcher = fetcher
) {
  const { queryKey, endpoint, enabled, staleTime, gcTime } = options;
  return {
    // The caller's queryKey already encodes the entity id; `endpoint` is a pure
    // function of that id, so it is intentionally not a separate key dependency.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: () => (endpoint ? fetch(endpoint) : null),
    enabled: enabled ?? !!endpoint,
    staleTime,
    gcTime,
  };
}

export function useEntityQuery<T>(options: UseEntityQueryOptions) {
  const { data, isLoading, isFetching, isError, refetch } = useQuery(buildEntityQueryArgs(options));

  // Hand back data exactly as React Query yields it (T | undefined). Wrappers that
  // historically advertised `T | null` re-assert that themselves, preserving each
  // call site's original contract.
  return { data: data as T | undefined, isLoading, isFetching, isError, refetch };
}
